﻿import map from "lodash/map";

import { CATEGORY_PREFIX, SUB_CATEGORY_DELIMITER } from "app/common/services/categoryService";
import type { ICategories, ICategory, IFlattenedCategory } from "app/common/services/categoryService.types";
import { swSettings } from "app/common/services/swSettings";

import type { Industries } from "../types";

/**
 * @deprecated Builds the legacy 2-level category list, encoding ids as `Main~Sub` display names.
 * Superseded by `domain/industry/utils/taxonomy.ts` (SI-8780).
 */
interface IndustryService {
    getIndustries: (industries: Industries) => ICategories;
    getFlattenedIndustriesList: (industries: Industries) => IFlattenedCategory[];
}

const createIndustryService = (): IndustryService => {
    function isInactive(id: string) {
        return swSettings.current.isDemo === true && id !== swSettings.current.demo.category;
    }

    function getIndustries(industries: Industries) {
        if (!industries) {
            return [];
        }

        return Object.keys(industries).map((industry: string): ICategory => {
            const industryUnderscore = industry.replace(/ /g, "_").replace(/&/g, "and");

            const sons = industries[industry].reduce(
                (prevSons, son) => ({
                    ...prevSons,
                    [son]: {
                        name: son,
                        sons: {},
                    },
                }),
                {},
            );
            const children = map(sons, (_, sonName) => {
                const id = `${industry}~${sonName}`;
                return {
                    id,
                    categoryId: id,
                    text: sonName,
                    inactive: isInactive(id),
                    getText: () => sonName,
                    getChildrenText: () => sonName,
                    isCustomCategory: false,
                    isOldCategory: false,
                    forApi: `${CATEGORY_PREFIX}${industry}${SUB_CATEGORY_DELIMITER}${sonName}`,
                    forUrl: id,
                    forDisplayApi: sonName,
                    icon: `sprite-category ${industryUnderscore}`,
                };
            });

            return {
                children,
                sons,
                text: industry,
                id: industry,
                categoryId: industry,
                icon: `sprite-category ${industryUnderscore}`,
                cssClass: "parent-category",
                inactive: isInactive(industry),
                isCustomCategory: false,
                forApi: `${CATEGORY_PREFIX}${industry}`,
                forUrl: industry,
                forDisplayApi: industry,
                lastUpdated: null,
            };
        });
    }

    function getFlattenedIndustriesList(industries: Industries): IFlattenedCategory[] {
        return this.getIndustries(industries).reduce((items: IFlattenedCategory[], item: ICategory) => {
            const children = (item.children || []).map((child: ICategory) => ({
                ...child,
                isChild: true,
                parentItem: item,
            }));

            return items.concat({ ...item, isChild: false }, children);
        }, []);
    }

    return {
        getIndustries,
        getFlattenedIndustriesList,
    };
};

export default createIndustryService();
